import React, { useState } from 'react';
import { DataTitle, DataValue, NoData, PageCard, Poppins, Settings, Spacer, TitleWithBack } from 'src/common';
import ExpandCollapse from 'src/common/ExpandCollapse';
import styled from 'styled-components';
import CommonMenu from 'src/components/CommonMenu';
import { formatCurrency, formatDate, formatUrl } from 'src/utils/misc';
import { OpenInNew } from '@mui/icons-material';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import useVersion from 'src/hooks/useVersion';
import { VersionHeader } from 'src/hooks/useVersion/comps';
import { TLLink } from 'src/pages/DashboardPage/comps';
import { useStateSelector } from 'src/redux';
import { IncidentQuery, ObjectKind } from 'src/graphql/_generated';
import { queryCache } from 'react-query';
import { QKeys } from 'src/api/types';
import { CommentsModal } from 'src/components/modals';
import QuickActionsMenu from 'src/components/QuickActionsMenu';
import { useAuth } from 'src/state/auth';
import _ from 'lodash';

const WhiteBox = styled(PageCard)`
  padding-bottom: 0;
  .head {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &__actions {
      display: flex;
      align-items: center;
      grid-gap: 20px;
    }
  }

  .grid-view {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: 50px;
    max-width: 900px;
  }
  .panel {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    grid-gap: 15px;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .grid-view {
      grid-template-columns: 1fr 1fr;
      grid-gap: 20px;
    }
  }
`;

type ModalId = 'comments';

interface MetaSheetProps {
  data: NonNullable<IncidentQuery['incident']>;
  editable?: { onEditSelect: () => void; onDeleteSelect: () => void };
  children?: React.ReactNode;
}

const MetaSheet: React.FC<MetaSheetProps> = ({ data, editable, children }) => {
  const colors = useStateSelector(({ theme }) => theme.colors);
  const [anchorEl, setAnchorEl] = useState<React.MouseEvent['currentTarget'] | null>(null);
  const [modalId, setModalId] = useState<ModalId | null>(null);
  const { versionId, version } = useVersion();
  const { features } = useAuth();

  const onMenuClick = (e: React.MouseEvent<HTMLLIElement>, cb: () => void) => {
    e.stopPropagation();
    handleClose();
    cb();
  };

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      {modalId === 'comments' && (
        <CommentsModal
          onClose={() => setModalId(null)}
          subTitle={{ title: ObjectKind.Incident, value: data.name }}
          eventData={{ objectType: ObjectKind.Incident, objectId: data.id }}
          refetch={() => queryCache.invalidateQueries([QKeys.Audit])}
        />
      )}

      <WhiteBox>
        <div className="head">
          <TitleWithBack title="SECURITY INCIDENT" gradient />
          {editable && !versionId && (
            <div className="head__actions">
              <QuickActionsMenu
                actions={[
                  {
                    type: 'EVENT_ACTION',
                    disabled: !features['app.events'] || !!versionId,
                    onClick: () => {
                      setModalId('comments');
                      mpEvent(MPEvents.ButtonClick, { button: 'QuickActions-Comments', tags: ['INCIDENT'] });
                    },
                  },
                ]}
              />
              <Settings
                active={!!anchorEl}
                onClick={(e) => {
                  e.stopPropagation();
                  setAnchorEl(e.currentTarget);
                }}
                data-cy="common-menu"
              />
              <CommonMenu
                onEditClick={(e) => onMenuClick(e, () => editable.onEditSelect())}
                onDeleteClick={(e) => onMenuClick(e, () => editable.onDeleteSelect())}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              />
            </div>
          )}
        </div>
        <Spacer $px={30} />
        <Poppins px={32} weight={600} data-cy="incident-title">
          {data.name}
        </Poppins>
        {version && (
          <>
            <Spacer $px={5} />
            <VersionHeader version={version} />
          </>
        )}
        <Spacer $px={30} />
        <div className="grid-view">
          <div>
            <DataTitle>REFERENCE ID</DataTitle>
            {data.ref ? (
              <DataValue>
                {formatUrl(data.source) ? (
                  <a
                    className="table-link"
                    data-cy="refid-link"
                    href={formatUrl(data.source)}
                    target="_blank"
                    onClick={() =>
                      mpEvent(MPEvents.Link, {
                        source: { value: ['Incident'], params: { id: data.id } },
                        destination: { value: ['External URL'] },
                        tags: ['INCIDENT'],
                      })
                    }
                  >
                    {data.ref}{' '}
                    <OpenInNew
                      css={`
                        font-size: 14px !important;
                        color: ${colors.prussianBlue};
                      `}
                    />
                  </a>
                ) : (
                  data.ref
                )}
              </DataValue>
            ) : (
              <NoData />
            )}
          </div>
          <div>
            <DataTitle>TYPE</DataTitle>
            {data.incidentType ? <DataValue>{data.incidentType}</DataValue> : <NoData />}
          </div>
          <div>
            <DataTitle>SEVERITY</DataTitle>
            {data.severity ? <DataValue>{data.severity}</DataValue> : <NoData />}
          </div>
        </div>
        <Spacer $px={40} />
        <div className="grid-view">
          <div>
            <DataTitle>OWNER</DataTitle>
            {data.owner?.text ? <DataValue>{data.owner.text}</DataValue> : <NoData />}
          </div>
          <div>
            <DataTitle>DATE OF INCIDENT</DataTitle>
            {data.dateOfIncident ? (
              <DataValue>
                <TLLink date={data.dateOfIncident}>{formatDate(data.dateOfIncident)}</TLLink>
              </DataValue>
            ) : (
              <NoData />
            )}
          </div>
        </div>
        <Spacer $px={40} />
        <div className="grid-view">
          <div>
            <DataTitle>IMPACT</DataTitle>
            {data.impact ? <DataValue>{data.impact}</DataValue> : <NoData />}
          </div>
          <div>
            <DataTitle>IMPACT - LOWER</DataTitle>
            {_.isNumber(data.financialImpactLower) ? (
              <DataValue>{formatCurrency(data.financialImpactLower || 0)}</DataValue>
            ) : (
              <NoData />
            )}
          </div>
          <div>
            <DataTitle>IMPACT - UPPER</DataTitle>
            {_.isNumber(data.financialImpactUpper) ? (
              <DataValue>{formatCurrency(data.financialImpactUpper || 0)}</DataValue>
            ) : (
              <NoData />
            )}
          </div>
        </div>
        <Spacer $px={40} />
        <div>
          <DataTitle>DESCRIPTION</DataTitle>
          {data.description ? <ExpandCollapse text={data.description} /> : <NoData />}
        </div>
        <Spacer $px={0} />
        {children}
      </WhiteBox>
    </>
  );
};

export default MetaSheet;
